import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useClient } from '@liveblocks/react'
import { Client, InboxNotificationData, ThreadData } from '@liveblocks/client'
import {
  BaseUserMeta,
  InboxNotificationCustomData,
  InboxNotificationDeleteInfo,
  InboxNotificationTextMentionData,
  InboxNotificationThreadData as LbInboxNotificationThreadData,
  ThreadDeleteInfo,
} from '@liveblocks/core'
import { getNotificationDate } from '@/app/core/components/notification/utils'

export type UserThreadNotification = LbInboxNotificationThreadData & {
  thread?: ThreadData<{ questionId?: string }>
}

export type UserNotification =
  | UserThreadNotification
  | InboxNotificationCustomData
  | InboxNotificationTextMentionData

type UserNotificationContext = {
  notifications: UserNotification[]
  isLoading: boolean
  markInboxNotificationAsRead: (notificationId: string) => Promise<void>
  markAllInboxNotificationsAsRead: () => Promise<void>
  deleteInboxNotification: (notificationId: string) => Promise<void>
  deleteAllInboxNotifications: () => Promise<void>
}

const context = createContext<UserNotificationContext | null>(null)

// Add thread data to the thread notifications
const formatNotification = (
  notification: InboxNotificationData,
  threads: ThreadData<{ questionId?: string }>[]
): UserNotification => {
  if (notification.kind !== 'thread') {
    return notification
  }

  const thread = threads.find((thread) => thread.id === notification.threadId)

  return {
    ...notification,
    thread,
  }
}

const fetchAllNotifications = async (
  client: Client<BaseUserMeta>
): Promise<{
  notifications: UserNotification[]
  lastRequestedAt: Date
}> => {
  const result = []

  let cursor: string | undefined = undefined
  let lastRequestedAt = new Date()

  // Iterate to fetch all the user notifications
  do {
    const { inboxNotifications, threads, nextCursor, requestedAt } =
      await client.getInboxNotifications({
        cursor,
      })

    result.push(
      inboxNotifications.map((notification) =>
        formatNotification(notification, threads)
      )
    )

    lastRequestedAt = requestedAt
    cursor = nextCursor ?? undefined
  } while (cursor)

  return {
    notifications: result
      .flat()
      .sort(
        (a, b) =>
          getNotificationDate(b).getTime() - getNotificationDate(a).getTime()
      ),
    lastRequestedAt,
  }
}

const applyNotificationUpdates = (
  notifications: UserNotification[],
  updatedNotifications: InboxNotificationData[],
  updatedThreads: ThreadData<{
    questionId?: string
  }>[]
) => {
  for (const updatedNotification of updatedNotifications) {
    const index = notifications.findIndex(
      (notification) => notification.id === updatedNotification.id
    )

    if (index !== -1) {
      notifications[index] = {
        ...notifications[index],
        ...updatedNotification,
      }
    } else if (updatedNotification.kind === 'thread') {
      const thread = updatedThreads.find(
        (thread) => thread.id === updatedNotification.threadId
      )

      notifications.push({
        ...updatedNotification,
        thread,
      })
    } else {
      notifications.push(updatedNotification)
    }
  }

  for (const updatedThread of updatedThreads) {
    const index = notifications.findIndex(
      (notification) =>
        notification.kind === 'thread' &&
        notification.thread?.id === updatedThread.id
    )

    if (index !== -1) {
      ;(notifications[index] as UserThreadNotification).thread = updatedThread
    }
  }
}

const applyNotificationDeletions = (
  notifications: UserNotification[],
  deletedNotifications: InboxNotificationDeleteInfo[],
  deletedThreads: ThreadDeleteInfo[]
) => {
  for (const deletedNotification of deletedNotifications) {
    const index = notifications.findIndex(
      (notification) => notification.id === deletedNotification.id
    )

    if (index !== -1) {
      notifications.splice(index, 1)
    }
  }

  for (const deletedThread of deletedThreads) {
    const index = notifications.findIndex(
      (notification) =>
        notification.kind === 'thread' &&
        notification.thread?.id === deletedThread.id
    )

    if (index !== -1) {
      notifications[index] = {
        ...notifications[index],
        thread: undefined,
      } as UserThreadNotification
    }
  }
}

const applyNotificationDelta = (
  notifications: UserNotification[],
  notificationDelta: {
    updated: InboxNotificationData[]
    deleted: InboxNotificationDeleteInfo[]
  },
  threadDelta: {
    updated: ThreadData<{
      questionId?: string
    }>[]
    deleted: ThreadDeleteInfo[]
  }
): UserNotification[] => {
  // No changes, so return the current notifications
  if (
    notificationDelta.updated.length === 0 &&
    threadDelta.updated.length === 0 &&
    notificationDelta.deleted.length === 0 &&
    threadDelta.deleted.length === 0
  ) {
    return notifications
  }

  const result = [...notifications]

  applyNotificationUpdates(
    result,
    notificationDelta.updated,
    threadDelta.updated
  )
  applyNotificationDeletions(
    result,
    notificationDelta.deleted,
    threadDelta.deleted
  )

  return result.sort(
    (a, b) =>
      getNotificationDate(b).getTime() - getNotificationDate(a).getTime()
  )
}

export function UserNotificationProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const client = useClient()

  const [notifications, setNotifications] = useState<UserNotification[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const lastRequestedAt = useRef<Date | null>(null)
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null)

  const loadNotifications = useCallback(
    async (client: Client<BaseUserMeta>) => {
      setIsLoading(true)

      try {
        const data = await fetchAllNotifications(client)

        lastRequestedAt.current = data.lastRequestedAt

        setNotifications(data.notifications)
      } catch (error) {
        console.warn('Error fetching notifications', { error })
      }

      setIsLoading(false)
    },
    []
  )

  const scheduleNextSync = useCallback(
    (client: Client<BaseUserMeta>) => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current)
      }

      refreshTimeout.current = setTimeout(async () => {
        if (!lastRequestedAt.current) {
          // Notifications have not been loaded yet
          // Load them for the first time
          await loadNotifications(client)
          scheduleNextSync(client)
          return
        }

        try {
          const { inboxNotifications, threads, requestedAt } =
            await client.getInboxNotificationsSince({
              since: lastRequestedAt.current,
            })

          lastRequestedAt.current = requestedAt

          setNotifications((prev) =>
            applyNotificationDelta(prev, inboxNotifications, threads)
          )
        } catch (error) {
          console.warn('Error fetching notifications delta', { error })
        }

        scheduleNextSync(client)
      }, 5000) // 5 seconds
    },
    [loadNotifications]
  )

  const markInboxNotificationAsRead = useCallback(
    async (notificationId: string) => {
      setNotifications((prev) => [
        ...prev.map((notification) => {
          if (notification.id !== notificationId) {
            return notification
          }

          return {
            ...notification,
            readAt: new Date(),
          }
        }),
      ])
      await client.markInboxNotificationAsRead(notificationId)
    },
    [client]
  )

  const markAllInboxNotificationsAsRead = useCallback(async () => {
    setNotifications((prev) =>
      prev.map((notification) => ({
        ...notification,
        readAt: new Date(),
      }))
    )
    await client.markAllInboxNotificationsAsRead()
  }, [client])

  const deleteInboxNotification = useCallback(
    async (notificationId: string) => {
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== notificationId)
      )
      await client.deleteInboxNotification(notificationId)
    },
    [client]
  )

  const deleteAllInboxNotifications = useCallback(async () => {
    setNotifications([])
    await client.deleteAllInboxNotifications()
  }, [client])

  useEffect(() => {
    const load = async () => {
      await loadNotifications(client)
      scheduleNextSync(client)
    }

    load()

    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current)
      }
    }
  }, [client, loadNotifications, scheduleNextSync])

  const value = useMemo(
    () => ({
      notifications,
      isLoading,
      markInboxNotificationAsRead,
      markAllInboxNotificationsAsRead,
      deleteInboxNotification,
      deleteAllInboxNotifications,
    }),
    [
      notifications,
      isLoading,
      markInboxNotificationAsRead,
      markAllInboxNotificationsAsRead,
      deleteInboxNotification,
      deleteAllInboxNotifications,
    ]
  )

  return <context.Provider value={value}>{children}</context.Provider>
}

export const useUserNotifications = () => {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'useUserNotifications must be used within a UserNotificationProvider'
    )
  }

  return {
    notifications: contextValue.notifications,
    isLoading: contextValue.isLoading,
  }
}

export const useMarkInboxNotificationAsRead = () => {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'useMarkInboxNotificationAsRead must be used within a UserNotificationProvider'
    )
  }

  return contextValue.markInboxNotificationAsRead
}

export const useMarkAllInboxNotificationsAsRead = () => {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'useMarkAllInboxNotificationsAsRead must be used within a UserNotificationProvider'
    )
  }

  return contextValue.markAllInboxNotificationsAsRead
}

export const useDeleteInboxNotification = () => {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'useDeleteInboxNotification must be used within a UserNotificationProvider'
    )
  }

  return contextValue.deleteInboxNotification
}

export const useDeleteAllInboxNotifications = () => {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'useDeleteAllInboxNotifications must be used within a UserNotificationProvider'
    )
  }

  return contextValue.deleteAllInboxNotifications
}
